import { useState, useRef, useEffect } from 'react'
import { Pencil, Check, X, Loader2 } from 'lucide-react'
import { Input } from '@brent/core-ui/ui'
import { Textarea } from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { cn } from '@/lib/utils'

interface InlineEditProps {
  value: string
  onSave: (value: string) => Promise<void> | void
  placeholder?: string
  className?: string
  displayClassName?: string
  disabled?: boolean
  multiline?: boolean
}

export function InlineEdit({
  value,
  onSave,
  placeholder = 'Enter value...',
  className,
  displayClassName,
  disabled,
  multiline,
}: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isEditing) {
      const el = multiline ? textareaRef.current : inputRef.current
      if (el) {
        el.focus()
        el.select()
      }
    }
  }, [isEditing, multiline])

  useEffect(() => {
    setEditValue(value)
  }, [value])

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await onSave(editValue)
      setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (!multiline || e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (disabled) {
    return (
      <span className={cn('text-sm', displayClassName)}>
        {value || <span className="text-muted-foreground">{placeholder}</span>}
      </span>
    )
  }

  if (!isEditing) {
    return (
      <button
        className={cn(
          'group inline-flex items-center gap-1.5 text-sm hover:text-primary text-left',
          displayClassName,
          className,
        )}
        onClick={() => setIsEditing(true)}
        title="Click to edit"
      >
        {value || <span className="text-muted-foreground">{placeholder}</span>}
        <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-50" />
      </button>
    )
  }

  return (
    <div
      className={cn('flex gap-1', multiline ? 'flex-col items-start' : 'items-center', className)}
    >
      {multiline ? (
        <Textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="min-h-[60px] w-full text-sm"
          disabled={isSaving}
          rows={3}
        />
      ) : (
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-7 w-40 text-sm"
          disabled={isSaving}
        />
      )}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleCancel}
          disabled={isSaving}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
        {multiline && (
          <span className="text-xs text-muted-foreground ml-1">Ctrl+Enter to save</span>
        )}
      </div>
    </div>
  )
}
